import type { FlowComponent, JSX, ParentComponent, VoidComponent } from "solid-js";
import { createUniqueId, mergeProps, Show } from "solid-js";
import { t } from "i18next";
import InfoButton from "../infoButton/InfoButton";
import { Icon } from "../icon/Icon";

type HeaderProps = {
  id: string;
  label: string;
  info?: {
    title: string;
    description: string;
    alt: string;
  };
} & RequiredIndicatorProps;

type RequiredIndicatorProps = {
  required?: boolean;
};

type ErrorMessageProps = {
  errorMessage?: string;
};

type InputProps = Omit<HeaderProps, "id"> & ErrorMessageProps;
export type ExtendableInputProps = Omit<HeaderProps, "id"> & ErrorMessageProps;

const Input: FlowComponent<InputProps, (id: string) => JSX.Element> = (props) => {
  const id = createUniqueId();

  return (
    <div class="flex flex-col gap-1">
      <Header id={id} label={props.label} required={props.required} info={props.info} />
      {props.children(id)}
      <ErrorMessage errorMessage={props.errorMessage} />
    </div>
  );
};

export const Header: VoidComponent<HeaderProps> = (props) => (
  <div class="flex max-w-full min-w-0 items-center">
    <label for={props.id} class="truncate text-base-semibold text-secondary">
      {props.label}
      <RequiredIndicator required={props.required} />
    </label>
    <Show when={props.info}>
      {(info) => (
        <InfoButton
          title={info().title}
          classList={{ "ml-auto": true }}
          description={info().description}
          ariaLabel={info().alt}
        />
      )}
    </Show>
  </div>
);

export const RequiredIndicator: VoidComponent<RequiredIndicatorProps> = (props) => {
  const merged = mergeProps({ required: false }, props);

  return (
    <Show when={merged.required}>
      <span class="relative ml-1">
        <span class="text-status-error" inert>
          *
        </span>
        <span class="sr-only">({t(($) => $.REQUIRED_ARIA)})</span>
      </span>
    </Show>
  );
};

export const ErrorMessage: ParentComponent<ErrorMessageProps> = (props) => (
  <Show when={props.errorMessage}>
    <div class="flex gap-1 text-base-medium">
      <Icon class="mt-0.5 shrink-0 icon-status-error" name="exclamationCircle" type="solid" size="md" />
      <span class="wrap-anywhere text-status-error">{props.errorMessage}</span>
    </div>
  </Show>
);

export default Input;
